/* eslint-disable max-len */
import { variantProvider } from '../theme'
import { includePresets } from '@codeleap/common'
import { assignTextStyle } from './Text'
import {
  ActionIconComposition,
  ButtonComposition,
  DrawerComposition,
} from '@codeleap/web'
import { AvatarComposition } from './Avatar'

export type HeaderComposition =
  | 'wrapper'
  | 'innerWrapper'
  | `drawer${Capitalize<DrawerComposition>}`
  | `signInButton${Capitalize<ButtonComposition>}`
  | `menuIcon${Capitalize<ActionIconComposition>}`
  | 'navContentWrapper'
  | 'navItem'
  | 'navItem:selected'
  | 'logoWrapper'
  | 'logo'
  | 'profileWrapper'
  | 'profileInfos'
  | 'firstName'
  | 'email'
  | 'drawerLogo'
  | `avatar${Capitalize<AvatarComposition>}`

const createHeaderStyle =
  variantProvider.createVariantFactory<HeaderComposition>()

const presets = includePresets(s => createHeaderStyle(() => ({ wrapper: s })))

export const BREAKPOINT = 'tabletSmall'
const logoSize = 24

export const AppHeaderStyles = {
  ...presets,
  default: createHeaderStyle(theme => ({
    wrapper: {
      position: 'sticky',
      zIndex: theme.zIndexes.header,
      top: theme.spacing.value(0),
      left: theme.spacing.value(0),
      right: theme.spacing.value(0),
      backgroundColor: theme.colors.background + '55',
      backdropFilter: 'blur(4px)',
      borderBottom: `1px solid ${theme.colors.neutral3}`,
      ...theme.spacing.paddingVertical(2),
    },
    innerWrapper: {
      ...theme.presets.row,
      ...theme.presets.alignCenter,
    },
    logo: {
      width: logoSize * 8,

      [theme.media.down(BREAKPOINT)]: {
        content: 'url("data:image/png;base64,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")',
        width: logoSize,
        display: 'flex',
      },
    },
    logoWrapper: {
      marginRight: 'auto',

      [theme.media.down(BREAKPOINT)]: {
        marginRight: theme.spacing.value(0),
        display: 'flex',
        ...theme.presets.justifyCenter,
      },
    },
    drawerBox: {
      ...theme.presets.column,
      ...theme.presets.alignCenter,
      gap: theme.spacing.value(2),
      backgroundColor: theme.colors.background,
      alignItems: 'stretch',
      ...theme.presets.elevated,
    },
    drawerCloseButtonIcon: {
      color: theme.colors.neutral10,
    },
    drawerCloseButtonTouchableWrapper: {
      backgroundColor: 'transparent',
      ...theme.spacing.padding(0),
      marginLeft: 'auto',
    },
    profileWrapper: {
      borderRadius: theme.borderRadius.small,
      ...theme.border.neutral5({ width: 1 }),
      ...theme.presets.centerRow,
      ...theme.spacing.padding(2),
      ...theme.presets.fullWidth,
      ...theme.spacing.gap(2),
      ...theme.spacing.marginBottom(1),
    },
    profileInfos: {
      width: `calc(100% - ${
        theme.values.itemHeight.small + theme.spacing.value(3)
      }px)`,
      ...theme.presets.column,
      ...theme.presets.justifySpaceBetween,
      ...theme.spacing.gap(0.5),
    },
    email: {
      ...assignTextStyle('p3')(theme).text,
      color: theme.colors.neutral8,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    firstName: {
      ...assignTextStyle('h3')(theme).text,
    },
    signInButtonWrapper: {
      borderRadius: theme.borderRadius.medium,
      ...theme.spacing.paddingHorizontal(2),
      ...theme.spacing.paddingVertical(1),
      height: theme.values.itemHeight.small,

      [theme.media.down(BREAKPOINT)]: {
        ...theme.presets.fullWidth,
        height: theme.values.itemHeight.default,
      },
    },
    signInButtonText: {
      fontSize: theme.typography.base.styles.p3.size,
      fontWeight: '500',
    },
    signInButtonIcon: {
      width: theme.values.iconSize[1],
      height: theme.values.iconSize[1],

      [theme.media.down(BREAKPOINT)]: {
        width: theme.values.iconSize[2],
        height: theme.values.iconSize[2],
      },
    },
    menuIconTouchableWrapper: {
      display: 'none',
      marginLeft: 'auto',
      background: 'transparent',

      [theme.media.down(BREAKPOINT)]: {
        display: 'flex',
      },
    },
    menuIconIcon: {
      marginLeft: 'auto',
      color: theme.colors.neutral10,
    },
    navItem: {
      ...assignTextStyle('h4')(theme).text,
      ...theme.presets.textCenter,
      color: theme.colors.neutral9,
      fontWeight: '600',
      textDecoration: 'none',

      [theme.media.down(BREAKPOINT)]: {
        width: '100%',
        ...assignTextStyle('h5')(theme).text,
        ...theme.presets.textCenter,
        ...theme.spacing.padding(2),
        backgroundColor: theme.colors.transparent,
        borderRadius: theme.borderRadius.small,
        color: theme.colors.primary3,
        fontWeight: '400',
      },
    },
    'navItem:selected': {
      color: theme.colors.primary3,

      [theme.media.down(BREAKPOINT)]: {
        backgroundColor: theme.colors.primary1,
        color: theme.colors.primary3,
        fontWeight: '600',
      },
    },
    navContentWrapper: {
      gap: theme.spacing.value(4),
      ...theme.presets.center,

      [theme.media.down(BREAKPOINT)]: {
        display: 'none',
      },
    },
    drawerLogo: {
      ...theme.presets.absolute,
      top: theme.spacing.value(2),
    },
  })),
}
